'use client';

/**
 * What a note refers to: links, files and tickets, in one list.
 *
 * WHY ONE LIST AND NOT THREE SECTIONS
 *
 * Somebody reading a note wants what it points at. Splitting it into Links,
 * Documents and Tickets makes you check three places to find the one thing you
 * half-remember, and it puts the payer rule and the spreadsheet that implements
 * it on opposite sides of the panel when they belong next to each other.
 *
 * WHY A FILE IS A REFERENCE
 *
 * Uploads go to the media library, which already stores per organization,
 * already returns a URL, and is already backed up and scanned. The note holds
 * the reference. A second file store for notes would be a second thing to
 * expire, a second thing to get the retention wrong on, and the same file in
 * two places.
 */

import React, { useRef, useState } from 'react';
import { ExternalLink, FileUp, Link2, Loader2, Paperclip, Ticket, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  WorkflowAttachmentKind,
  WorkflowNoteAttachment,
  workflowsAPI,
} from '@/services/api/workflows';

/** Matches PROJ-123. Used only to tidy what somebody typed — a key that does
 *  not match is still accepted, because a Jira project can be named anything
 *  and refusing a real ticket to enforce a guess would be worse. */
const JIRA_KEY = /^[A-Z][A-Z0-9_]*-\d+$/i;

const KIND_ICON: Record<WorkflowAttachmentKind, React.ReactNode> = {
  link: <Link2 className="h-3.5 w-3.5" />,
  file: <Paperclip className="h-3.5 w-3.5" />,
  jira: <Ticket className="h-3.5 w-3.5" />,
};

function prettySize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface NoteAttachmentsProps {
  value: WorkflowNoteAttachment[];
  onChange: (next: WorkflowNoteAttachment[]) => void;
  /** Read-only on a saved note; editable while one is being written. */
  editable?: boolean;
}

export const NoteAttachments: React.FC<NoteAttachmentsProps> = ({ value, onChange, editable = true }) => {
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [jiraKey, setJiraKey] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const add = (item: WorkflowNoteAttachment) => onChange([...value, item]);
  const removeAt = (index: number) => onChange(value.filter((_, i) => i !== index));

  const addLink = () => {
    const url = linkUrl.trim();
    if (!url) return;
    // A bare domain is what people paste; without a scheme the browser treats
    // the href as a relative path and the link opens inside the app.
    const normalised = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    add({ kind: 'link', url: normalised, label: linkLabel.trim() || undefined });
    setLinkUrl('');
    setLinkLabel('');
  };

  const addJira = () => {
    const key = jiraKey.trim().toUpperCase();
    if (!key) return;
    add({ kind: 'jira', key });
    setJiraKey('');
  };

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    setError(null);
    try {
      // Sequentially, so a failure half way names which file failed instead of
      // leaving the list in a state nobody can explain.
      const added: WorkflowNoteAttachment[] = [];
      for (const file of Array.from(files)) {
        added.push(await workflowsAPI.uploadNoteFile(file));
      }
      onChange([...value, ...added]);
    } catch {
      setError('That file could not be uploaded. Nothing was attached.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div>
      {value.length > 0 && (
        <ul style={{ listStyle: 'none', margin: '0 0 8px', padding: 0, display: 'grid', gap: 4 }}>
          {value.map((item, index) => (
            <li
              key={`${item.kind}-${item.url ?? item.key}-${index}`}
              className="flex items-center gap-2 rounded border border-gray-200 px-2 py-1.5 text-sm dark:border-gray-700"
            >
              <span className="text-gray-400">{KIND_ICON[item.kind]}</span>

              {item.kind === 'jira' ? (
                <span className="font-mono text-xs font-semibold text-gray-800 dark:text-gray-200">
                  {item.key}
                </span>
              ) : (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="min-w-0 flex-1 truncate text-blue-600 hover:underline dark:text-blue-400"
                >
                  {item.label || item.url}
                </a>
              )}

              {item.kind === 'file' && item.size ? (
                <span className="shrink-0 text-xs text-gray-400">{prettySize(item.size)}</span>
              ) : null}

              {item.kind !== 'jira' && <ExternalLink className="h-3 w-3 shrink-0 text-gray-300" />}

              {editable && (
                <button
                  type="button"
                  onClick={() => removeAt(index)}
                  title="Remove"
                  className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-800"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!editable ? null : (
        <div className="grid gap-2">
          {error && <p className="text-xs text-red-600">{error}</p>}

          <div className="flex flex-wrap gap-2">
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  // Otherwise Enter submits the note and the link is lost with
                  // the form it was being typed into.
                  e.preventDefault();
                  addLink();
                }
              }}
              placeholder="Paste a link — a rule, a document, a dashboard"
              className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
            <input
              type="text"
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
              placeholder="What it is (optional)"
              className="w-44 rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
            <Button variant="outline" onClick={addLink} disabled={!linkUrl.trim()}>
              Add link
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              value={jiraKey}
              onChange={(e) => setJiraKey(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addJira();
                }
              }}
              placeholder="Ticket, e.g. TRUS-611"
              className="w-48 rounded-md border border-gray-300 px-2 py-1.5 font-mono text-sm uppercase dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
            <Button variant="outline" onClick={addJira} disabled={!jiraKey.trim()}>
              <Ticket className="mr-1 h-3.5 w-3.5" />
              Add ticket
            </Button>
            {jiraKey.trim() && !JIRA_KEY.test(jiraKey.trim()) && (
              <span className="self-center text-xs text-amber-700">
                That does not look like PROJ-123 — it will still be saved.
              </span>
            )}

            <div className="flex-1" />

            <input
              ref={fileRef}
              type="file"
              multiple
              hidden
              onChange={(e) => void upload(e.target.files)}
            />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileUp className="mr-1 h-3.5 w-3.5" />
              )}
              Attach files
            </Button>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            Documents, spreadsheets and links are stored with your organization&rsquo;s media, and the note
            keeps a reference to them.
          </p>
        </div>
      )}
    </div>
  );
};

export default NoteAttachments;
